import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { profileApiClient } from '../../services/profileApiClient';
import styles from './ProfileInfoPanel.module.css';

interface CommunicationPrefs {
  supportCommunications: boolean;
  productAnnouncements: boolean;
  technicalDocumentation: boolean;
  webinarsAndEvents: boolean;
  trainingAndLearning: boolean;
  commvaultNews: boolean;
  whitePapers: boolean;
  communityActivity: boolean;
}

interface ProfileInfoPanelProps {
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  mobilePhone?: string;
  address?: string;
  role?: string;
  preferences?: CommunicationPrefs;
}

const CheckIcon = ({ checked }: { checked: boolean }) => (
  <svg width="18" height="18" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    {checked ? (
      <>
        <circle cx="10" cy="10" r="10" fill="#28a745" />
        <path d="M6 10L9 13L14 7" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      </>
    ) : (
      <>
        <circle cx="10" cy="10" r="9" stroke="#555555" strokeWidth="1.75" />
        <line x1="4.5" y1="15.5" x2="15.5" y2="4.5" stroke="#555555" strokeWidth="1.75" strokeLinecap="round" />
      </>
    )}
  </svg>
);

export const ProfileInfoPanel: React.FC<ProfileInfoPanelProps> = ({
  firstName,
  lastName,
  email,
  phone,
  mobilePhone,
  address,
  role,
  preferences,
}) => {
  const fullName = `${firstName} ${lastName}`.trim();

  const [currentPrefs, setCurrentPrefs] = useState<CommunicationPrefs | undefined>(preferences);
  const [saveStatus, setSaveStatus] = useState<string | null>(null);
  const [currentRole, setCurrentRole] = useState(role || '');
  const [isUpdatingRole, setIsUpdatingRole] = useState(false);

  useEffect(() => {
    if (preferences) {
      setCurrentPrefs(preferences);
    }
  }, [preferences]);

  useEffect(() => {
    if (role) {
      setCurrentRole(role);
    }
  }, [role]);

  const handleRoleChange = async (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newRole = e.target.value;
    setCurrentRole(newRole);
    setIsUpdatingRole(true);
    setSaveStatus('Saving role...');
    try {
      await profileApiClient.updateRole(newRole);
      setSaveStatus('Role saved ✓');
      setTimeout(() => setSaveStatus(null), 2500);
    } catch (err) {
      console.error('Failed to update role in Dynamics CRM:', err);
      setSaveStatus('Error saving role');
      setTimeout(() => setSaveStatus(null), 3000);
    } finally {
      setIsUpdatingRole(false);
    }
  };

  const handleToggle = async (key: keyof CommunicationPrefs) => {
    if (!currentPrefs) return;
    const updated = {
      ...currentPrefs,
      [key]: !currentPrefs[key],
    };
    setCurrentPrefs(updated);
    setSaveStatus('Saving...');
    try {
      await profileApiClient.updatePreferences(updated);
      setSaveStatus('Saved ✓');
      setTimeout(() => setSaveStatus(null), 2500);
    } catch (err) {
      console.error('Failed to save communication preferences:', err);
      setSaveStatus('Error saving');
      setTimeout(() => setSaveStatus(null), 3000);
    }
  };

  return (
    <div className={styles.profilePanel}>
      <div className={styles.header}>
        <h3 className={styles.title}>Profile Information</h3>
        <Link to="/profile" className={styles.editButton}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z"></path>
          </svg>
        </Link>
      </div>
      
      <div className={styles.content}>
        <div className={styles.profileSummary}>
          <div className={styles.avatar}>
            {/* Using initials fallback if no image is provided */}
            <div className={styles.avatarInitials}>
              {firstName.charAt(0)}{lastName.charAt(0)}
            </div>
          </div>
          <p className={styles.message}>
            Keeping your profile information updated is an important step in maintaining a great experience with us.
          </p>
        </div>

        <div className={styles.details}>
          <div className={styles.detailItem}>
            <svg className={styles.icon} width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
              <circle cx="12" cy="7" r="4"></circle>
            </svg>
            <span>{fullName}</span>
          </div>
          <div className={styles.detailItem}>
            <svg className={styles.icon} width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
              <polyline points="22,6 12,13 2,6"></polyline>
            </svg>
            <span>{email}</span>
          </div>
          {phone && (
            <div className={styles.detailItem}>
              <svg className={styles.icon} width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
              </svg>
              <span>{phone.startsWith('Biz:') || phone.startsWith('Phone:') ? phone : `Biz: ${phone}`}</span>
            </div>
          )}
          {mobilePhone && (
            <div className={styles.detailItem}>
              <svg className={styles.icon} width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="5" y="2" width="14" height="20" rx="2" ry="2"></rect>
                <line x1="12" y1="18" x2="12.01" y2="18"></line>
              </svg>
              <span>{mobilePhone.startsWith('Mobile:') ? mobilePhone : `Mobile: ${mobilePhone}`}</span>
            </div>
          )}
          {address && (
            <div className={styles.detailItem}>
              <svg className={styles.icon} width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                <circle cx="12" cy="10" r="3"></circle>
              </svg>
              <span>{address}</span>
            </div>
          )}
          <div className={styles.detailItem}>
            <svg className={styles.icon} width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
              <circle cx="9" cy="7" r="4"></circle>
              <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
              <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
            </svg>
            <select
              className={styles.roleSelect}
              value={currentRole}
              onChange={handleRoleChange}
              disabled={isUpdatingRole}
              aria-label="User Role"
            >
              <option value="Backup Admin">Backup Admin</option>
              <option value="IT Administrator">IT Administrator</option>
              <option value="Security Admin">Security Admin</option>
              <option value="Compliance Admin">Compliance Admin</option>
              <option value="Read-Only User">Read-Only User</option>
            </select>
          </div>
        </div>

        {currentPrefs && (
          <div className={styles.preferencesSection}>
            <div className={styles.sectionTitleRow}>
              <h4 className={styles.sectionTitle}>Communication Preferences</h4>
              {saveStatus && <span className={styles.saveStatus}>{saveStatus}</span>}
            </div>
            <p className={styles.preferencesDescription}>
              Our communications are sent out via email. Click any option below to toggle your preferences:
            </p>
            <div className={styles.preferencesGrid}>
              <div 
                className={styles.preferenceItem} 
                onClick={() => handleToggle('supportCommunications')}
                role="button"
                tabIndex={0}
                title="Click to toggle Support Communications"
              >
                <CheckIcon checked={currentPrefs.supportCommunications} />
                <span>Support Communications</span>
              </div>
              <div 
                className={styles.preferenceItem} 
                onClick={() => handleToggle('productAnnouncements')}
                role="button"
                tabIndex={0}
                title="Click to toggle Commvault Announcements"
              >
                <CheckIcon checked={currentPrefs.productAnnouncements} />
                <span>Commvault Announcements</span>
              </div>
              <div 
                className={styles.preferenceItem} 
                onClick={() => handleToggle('whitePapers')}
                role="button"
                tabIndex={0}
                title="Click to toggle White-papers"
              >
                <CheckIcon checked={currentPrefs.whitePapers} />
                <span>White-papers</span>
              </div>
              <div 
                className={styles.preferenceItem} 
                onClick={() => handleToggle('technicalDocumentation')}
                role="button"
                tabIndex={0}
                title="Click to toggle Technical Documentation"
              >
                <CheckIcon checked={currentPrefs.technicalDocumentation} />
                <span>Technical Documentation</span>
              </div>
              <div 
                className={styles.preferenceItem} 
                onClick={() => handleToggle('webinarsAndEvents')}
                role="button"
                tabIndex={0}
                title="Click to toggle Webinars and Events"
              >
                <CheckIcon checked={currentPrefs.webinarsAndEvents} />
                <span>Webinars and Events</span>
              </div>
              <div 
                className={styles.preferenceItem} 
                onClick={() => handleToggle('communityActivity')}
                role="button"
                tabIndex={0}
                title="Click to toggle Community Activity"
              >
                <CheckIcon checked={currentPrefs.communityActivity} />
                <span>Community Activity</span>
              </div>
              <div 
                className={styles.preferenceItem} 
                onClick={() => handleToggle('trainingAndLearning')}
                role="button"
                tabIndex={0}
                title="Click to toggle Training and Learning"
              >
                <CheckIcon checked={currentPrefs.trainingAndLearning} />
                <span>Training and Learning</span>
              </div>
              <div 
                className={styles.preferenceItem} 
                onClick={() => handleToggle('commvaultNews')}
                role="button"
                tabIndex={0}
                title="Click to toggle Commvault News"
              >
                <CheckIcon checked={currentPrefs.commvaultNews} />
                <span>Commvault News</span>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
